import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useDateFilter } from "@/context/DateFilterContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Archive, Building2, Tag, MessageSquare, Lightbulb } from "lucide-react";
import { StarRating } from "@/components/StarRating";
import { CommentThread } from "@/components/CommentThread";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { toast } from "sonner";

const empty = { name: "", manufacturer: "", category: "", price: "", claims: "", differentiation: "", rating: 0 };

export default function Benchmarks() {
  const { start, end } = useDateFilter();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [commentFor, setCommentFor] = useState(null);

  const load = () => api.get("/benchmarks", { params: { start, end } }).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [start, end]);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (b) => { setForm({ ...empty, ...b }); setEditId(b.id); setOpen(true); };
  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    try {
      if (editId) await api.put(`/benchmarks/${editId}`, form);
      else await api.post("/benchmarks", form);
      setOpen(false); load(); toast.success(editId ? "Updated" : "Benchmark added");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const archive = async (id) => { await api.delete(`/benchmarks/${id}`); load(); toast.success("Archived"); };
  const del = async (id) => { await api.delete(`/benchmarks/${id}`, { params: { permanent: true } }); load(); toast.success("Benchmark deleted"); };

  return (
    <div className="space-y-6" data-testid="benchmarks-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Benchmark Products</h1>
          <p className="text-muted-foreground">Gold-standard products, competitor services & market intelligence.</p>
        </div>
        <Button onClick={openNew} data-testid="new-benchmark-btn"><Plus size={16} className="mr-1" /> New Benchmark</Button>
      </div>

      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {items.length === 0 && <p className="text-muted-foreground">No benchmarks in this period.</p>}
        {items.map((b) => (
          <Card key={b.id} className="flex flex-col p-5 border-slate-200 shadow-sm transition-transform hover:-translate-y-0.5" data-testid={`benchmark-card-${b.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-head font-bold text-slate-900">{b.name}</h3>
                <p className="text-xs text-muted-foreground inline-flex items-center gap-1"><Building2 size={12} /> {b.manufacturer || "—"}</p>
              </div>
              <div className="flex gap-1">
                <button onClick={() => openEdit(b)} className="text-slate-400 hover:text-primary" data-testid={`edit-benchmark-${b.id}`}><Pencil size={15} /></button>
                <button onClick={() => archive(b.id)} className="text-slate-400 hover:text-amber-600" data-testid={`archive-benchmark-${b.id}`}><Archive size={15} /></button>
                <ConfirmDelete onConfirm={() => del(b.id)} name={b.name} testid={`delete-benchmark-${b.id}`} />
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between">
              <span className="inline-flex items-center gap-1 text-xs text-secondary"><Tag size={12} /> {b.category || "—"}</span>
              <StarRating value={b.rating} readOnly size={14} testid={`benchmark-rating-${b.id}`} />
            </div>
            <p className="mt-2 font-head text-lg font-bold text-primary">{b.price || "Price N/A"}</p>
            {b.claims && <p className="mt-2 text-sm text-slate-600"><span className="font-medium text-slate-800">Claims: </span>{b.claims}</p>}
            {b.differentiation && (
              <div className="mt-3 rounded-lg bg-accent p-3 text-xs text-accent-foreground">
                <p className="font-semibold inline-flex items-center gap-1"><Lightbulb size={12} /> Our Edge</p>
                <p className="mt-1">{b.differentiation}</p>
              </div>
            )}
            <Button variant="outline" size="sm" className="mt-4 self-start" onClick={() => setCommentFor(b)} data-testid={`comments-benchmark-${b.id}`}>
              <MessageSquare size={14} className="mr-1" /> Comments & Ratings
            </Button>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="benchmark-modal">
          <DialogHeader><DialogTitle>{editId ? "Edit Benchmark" : "New Benchmark"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Product / Service</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="benchmark-name-input" /></div>
              <div><Label>Manufacturer / Brand</Label><Input value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} data-testid="benchmark-manufacturer-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Laser, Anti-aging..." data-testid="benchmark-category-input" /></div>
              <div><Label>Market Price / Cost</Label><Input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="$1,200 / session" data-testid="benchmark-price-input" /></div>
            </div>
            <div><Label>Key Claims & Ingredients / Specs</Label><Textarea rows={2} value={form.claims} onChange={(e) => setForm({ ...form, claims: e.target.value })} data-testid="benchmark-claims-input" /></div>
            <div><Label>Differentiation Opportunity</Label><Textarea rows={2} value={form.differentiation} onChange={(e) => setForm({ ...form, differentiation: e.target.value })} placeholder="How can we do it better?" data-testid="benchmark-diff-input" /></div>
            <div><Label>Internal Rating</Label><div className="mt-1"><StarRating value={form.rating} onChange={(v) => setForm({ ...form, rating: v })} size={22} testid="benchmark-rating-input" /></div></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-benchmark-btn">{editId ? "Save Changes" : "Add Benchmark"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!commentFor} onOpenChange={(o) => !o && setCommentFor(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg" data-testid="benchmark-comments-modal">
          <DialogHeader><DialogTitle>{commentFor?.name} — Reviews</DialogTitle></DialogHeader>
          {commentFor && <CommentThread entityType="benchmark" entityId={commentFor.id} withRating />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
